import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Scale } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/contexts/AuthContext";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(loc.state?.error || "");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(email, password);
      nav("/dashboard");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-2 bg-white">
      <div className="hidden md:block relative bg-[#0B1B3D]">
        <div className="absolute inset-0" style={{
          backgroundImage: "url(https://images.pexels.com/photos/8730975/pexels-photo-8730975.jpeg)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          opacity: 0.25,
        }} />
        <div className="relative z-10 p-12 flex flex-col h-full text-white">
          <Link to="/" className="flex items-center gap-2" data-testid="brand-link">
            <Scale className="w-5 h-5" strokeWidth={1.75} />
            <span className="font-serif text-xl font-medium">FOIA Assist</span>
          </Link>
          <div className="mt-auto">
            <p className="font-serif text-4xl leading-tight max-w-md">
              &ldquo;Every response. Every redaction. Every action. Logged, attributed, defensible.&rdquo;
            </p>
            <p className="text-xs uppercase tracking-widest text-[#C5A059] mt-6">— Court-ready by design</p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 md:p-16">
        <div className="w-full max-w-md">
          <div className="font-mono text-xs uppercase tracking-widest text-slate-500 mb-3">Public Agency Sign-in</div>
          <h1 className="font-serif text-4xl tracking-tight font-medium mb-8">Welcome back.</h1>

          <form onSubmit={onSubmit} className="space-y-5">
            <div>
              <label className="fa-label">Work Email</label>
              <input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="fa-input"
                placeholder="jane.smith@city.gov"
                required
              />
            </div>
            <div>
              <label className="fa-label">Password</label>
              <input
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="fa-input"
                required
              />
            </div>
            {error && (
              <div className="fa-card p-3 border-red-200 bg-red-50 text-red-700 text-sm" data-testid="login-error">{error}</div>
            )}
            <button data-testid="login-submit-button" type="submit" disabled={busy} className="fa-btn-primary w-full justify-center">
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <div className="flex items-center gap-4 my-6">
            <div className="h-px bg-slate-200 flex-1" />
            <span className="text-xs uppercase tracking-widest text-slate-400">or</span>
            <div className="h-px bg-slate-200 flex-1" />
          </div>

          <button data-testid="login-google-button" onClick={googleLogin} className="fa-btn-secondary w-full justify-center">
            <svg width="16" height="16" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5h-1.9V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.5 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.5-5.2l-6.2-5.2C29.3 35 26.8 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.6 39.5 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.2-4.1 5.5l6.2 5.2c-.4.4 6.6-4.8 6.6-14.7 0-1.2-.1-2.4-.4-3.5z"/></svg>
            Continue with Google
          </button>

          <p className="text-sm text-slate-600 mt-8">
            New to FOIA Assist?{" "}
            <Link to="/register" className="text-[#0B1B3D] underline decoration-[#C5A059] decoration-2 underline-offset-4" data-testid="login-to-register-link">
              Start your 45-day trial
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
